import { useState } from 'react';
import {
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
} from '@simplewebauthn/browser';
import { Fingerprint, KeyRound, ShieldCheck, Smartphone, Trash2 } from 'lucide-react';
import type { TotpSetupResponse } from '@proerp/shared';
import { Badge, Button, FormField, Input, Spinner } from '@proerp/ui';
import { PageHeader, SectionCard } from '@/components/PageLayout';
import { QrCanvas } from '@/components/QrCanvas';
import { describePortalError } from '@/lib/apiClient';
import { guessDeviceLabel, logout } from '@/lib/auth';
import { validateNewPassword } from '@/lib/passwordRules';
import {
  useChangePortalPassword,
  useConfirmPortalTotp,
  useDeletePortalPasskey,
  useEnrollPortalTotpOptions,
  usePortalPasskeys,
  useRegisterPortalPasskey,
  useRegisterPortalPasskeyOptions,
  useRemovePortalTotp,
} from '@/lib/usePortalSecurity';
import { usePortalUser } from '@/lib/usePortalUser';

const dateTime = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Europe/Berlin',
});

/**
 * Der eigene Zugang (ADR 0034/0085). Bis hierher konnte ein Partner an seinem
 * Konto nichts ändern: Wer ein neues Telefon hatte, musste bei uns anrufen, und
 * wer sich beim Erstlogin für den Authenticator entschieden hatte, kam nie mehr
 * an einen Passkey. Die Endpunkte dafür gab es serverseitig von Anfang an — es
 * fehlte nur die Oberfläche.
 *
 * Beide Faktoren stehen gleichrangig nebeneinander (ADR 0034): Passkey ODER
 * Authenticator, mindestens einer. Der **letzte** Faktor lässt sich nicht
 * entfernen — sonst sperrt man sich selbst aus. Das erzwingt das Backend; hier
 * wird der Knopf zusätzlich gesperrt und der Grund hingeschrieben, statt den
 * Nutzer in eine Fehlermeldung laufen zu lassen.
 */
export function SettingsPage() {
  const user = usePortalUser();
  const passkeys = usePortalPasskeys();

  const isTotpEnabled = user.data?.isTotpEnabled ?? false;
  const items = passkeys.data ?? [];
  /**
   * Wie viele zweite Faktoren gibt es? Bei genau einem ist er der letzte — dann
   * darf weder er noch der Authenticator weg.
   */
  const factorCount = items.length + (isTotpEnabled ? 1 : 0);
  const isLastFactor = factorCount <= 1;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Zugang"
        title="Einstellungen"
        subtitle="Anmeldung absichern und Passwort ändern. Alles hier gilt nur für deinen eigenen Zugang."
      />

      <SectionCard
        title="Anmeldung"
        description="Für die Anmeldung braucht es neben dem Passwort einen zweiten Faktor — einen Passkey oder einen Authenticator. Du kannst beides haben."
      >
        <div className="flex flex-col gap-6">
          <PasskeySection
            items={items}
            isLoading={passkeys.isLoading}
            isLastFactor={isLastFactor}
            onChanged={() => void passkeys.refetch()}
          />
          <TotpSection
            isEnabled={isTotpEnabled}
            isLastFactor={isLastFactor}
            onChanged={() => void user.refetch()}
          />
        </div>
      </SectionCard>

      <SectionCard
        title="Passwort"
        description="Nach dem Ändern wirst du neu angemeldet — das Passwort beendet alle laufenden Sitzungen, auch diese."
      >
        <PasswordSection />
      </SectionCard>

      <SectionCard title="Dein Zugang" description="Diese Angaben pflegen wir für dich.">
        <dl className="grid gap-3 sm:grid-cols-2">
          <Fact label="Anmeldung mit" value={user.data?.email ?? '…'} />
          <Fact label="Partner" value={user.data?.partnerName ?? '…'} />
          <Fact
            label="Freigeschaltete Sichten"
            value={
              [
                user.data?.canSeeForecast ? 'Bedarfsvorschau' : null,
                user.data?.canSeeAssembly ? 'Montagen' : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'keine'
            }
          />
        </dl>
        <p className="mt-4 text-sm text-muted-foreground">
          Stimmt etwas nicht — falsche Adresse, fehlende Sicht, ein Kollege braucht einen eigenen
          Zugang? Melde dich bei deiner Ansprechpartnerin bei PROBIG.
        </p>
      </SectionCard>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{value}</dd>
    </div>
  );
}

/** Meldung unter einem Abschnitt — Erfolg und Fehler an derselben Stelle. */
function Note({ tone, children }: { tone: 'success' | 'danger'; children: React.ReactNode }) {
  return (
    <p
      role={tone === 'danger' ? 'alert' : 'status'}
      className={tone === 'danger' ? 'text-sm text-danger-600' : 'text-sm text-success-600'}
    >
      {children}
    </p>
  );
}

function PasskeySection({
  items,
  isLoading,
  isLastFactor,
  onChanged,
}: {
  items: { id: string; deviceLabel: string; createdAt: string; lastUsedAt: string | null }[];
  isLoading: boolean;
  isLastFactor: boolean;
  onChanged: () => void;
}) {
  const registerOptions = useRegisterPortalPasskeyOptions();
  const register = useRegisterPortalPasskey();
  const remove = useDeletePortalPasskey();

  const [isAdding, setIsAdding] = useState(false);
  const [deviceLabel, setDeviceLabel] = useState('');
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const isBusy = registerOptions.isPending || register.isPending;

  async function addPasskey(): Promise<void> {
    setMessage(null);
    try {
      const options = await registerOptions.mutateAsync();
      // Der Aufruf öffnet die Abfrage des Geräts (Face ID, Fingerabdruck, PIN).
      const attestationResponse = await startRegistration({
        optionsJSON: options.data.options as PublicKeyCredentialCreationOptionsJSON,
      });
      await register.mutateAsync({
        challengeToken: options.data.challengeToken,
        attestationResponse,
        deviceLabel: deviceLabel.trim() || guessDeviceLabel(),
      });
      setIsAdding(false);
      setDeviceLabel('');
      setMessage({ tone: 'success', text: 'Passkey hinzugefügt.' });
      onChanged();
    } catch (error) {
      setMessage({
        tone: 'danger',
        text: describePortalError(error) || 'Passkey konnte nicht hinzugefügt werden.',
      });
    }
  }

  async function removePasskey(id: string, label: string): Promise<void> {
    if (!window.confirm(`Passkey „${label}" wirklich entfernen?`)) return;
    setMessage(null);
    try {
      await remove.mutateAsync(id);
      setMessage({ tone: 'success', text: 'Passkey entfernt.' });
      onChanged();
    } catch (error) {
      setMessage({ tone: 'danger', text: describePortalError(error) });
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Fingerprint className="size-4 text-muted-foreground" aria-hidden />
          Passkeys
        </h3>
        {!isAdding && (
          <Button type="button" variant="secondary" size="sm" onClick={() => setIsAdding(true)}>
            Passkey hinzufügen
          </Button>
        )}
      </header>

      <p className="text-sm text-muted-foreground">
        Ein Passkey ist die Entsperrung deines Geräts — Face ID, Fingerabdruck oder Geräte-PIN.
        Für jedes Gerät, mit dem du dich anmeldest, brauchst du einen eigenen.
      </p>

      {isLoading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner aria-hidden /> Passkeys werden geladen …
        </p>
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
          Noch kein Passkey hinterlegt.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2"
            >
              <Smartphone className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{item.deviceLabel}</p>
                <p className="text-xs text-muted-foreground">
                  Hinzugefügt {dateTime.format(new Date(item.createdAt))}
                  {item.lastUsedAt
                    ? ` · zuletzt benutzt ${dateTime.format(new Date(item.lastUsedAt))}`
                    : ' · noch nicht benutzt'}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isLastFactor || remove.isPending}
                title={
                  isLastFactor
                    ? 'Das ist dein einziger zweiter Faktor — richte zuerst einen weiteren ein.'
                    : undefined
                }
                onClick={() => void removePasskey(item.id, item.deviceLabel)}
              >
                <Trash2 className="size-4" aria-hidden />
                Entfernen
              </Button>
            </li>
          ))}
        </ul>
      )}

      {isAdding && (
        <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
          <FormField label="Name des Geräts" htmlFor="passkey-label">
            <Input
              id="passkey-label"
              value={deviceLabel}
              placeholder={guessDeviceLabel()}
              onChange={(event) => setDeviceLabel(event.target.value)}
            />
          </FormField>
          <p className="text-sm text-muted-foreground">
            Der Name hilft nur dir, deine Geräte auseinanderzuhalten. Nach dem Klick fragt dein
            Gerät nach Face ID, Fingerabdruck oder PIN.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={isBusy} onClick={() => void addPasskey()}>
              {isBusy ? <Spinner aria-hidden /> : <ShieldCheck className="size-4" aria-hidden />}
              Passkey einrichten
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={isBusy}
              onClick={() => {
                setIsAdding(false);
                setDeviceLabel('');
              }}
            >
              Abbrechen
            </Button>
          </div>
        </div>
      )}

      {message && <Note tone={message.tone}>{message.text}</Note>}
    </section>
  );
}

function TotpSection({
  isEnabled,
  isLastFactor,
  onChanged,
}: {
  isEnabled: boolean;
  isLastFactor: boolean;
  onChanged: () => void;
}) {
  const enrollOptions = useEnrollPortalTotpOptions();
  const confirm = useConfirmPortalTotp();
  const remove = useRemovePortalTotp();

  const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  async function start(): Promise<void> {
    setMessage(null);
    try {
      const response = await enrollOptions.mutateAsync();
      setSetup(response.data);
      setCode('');
    } catch (error) {
      setMessage({ tone: 'danger', text: describePortalError(error) });
    }
  }

  async function confirmCode(): Promise<void> {
    setMessage(null);
    try {
      await confirm.mutateAsync(code.trim());
      setSetup(null);
      setCode('');
      setMessage({ tone: 'success', text: 'Authenticator eingerichtet.' });
      onChanged();
    } catch (error) {
      setMessage({ tone: 'danger', text: describePortalError(error) });
    }
  }

  async function disable(): Promise<void> {
    if (!window.confirm('Authenticator wirklich entfernen?')) return;
    setMessage(null);
    try {
      await remove.mutateAsync();
      setMessage({ tone: 'success', text: 'Authenticator entfernt.' });
      onChanged();
    } catch (error) {
      setMessage({ tone: 'danger', text: describePortalError(error) });
    }
  }

  return (
    <section className="flex flex-col gap-3 border-t border-border pt-6">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-medium text-foreground">
          <KeyRound className="size-4 text-muted-foreground" aria-hidden />
          Authenticator
          {isEnabled && <Badge tone="success">Eingerichtet</Badge>}
        </h3>
        {isEnabled ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isLastFactor || remove.isPending}
            title={
              isLastFactor
                ? 'Das ist dein einziger zweiter Faktor — richte zuerst einen Passkey ein.'
                : undefined
            }
            onClick={() => void disable()}
          >
            <Trash2 className="size-4" aria-hidden />
            Entfernen
          </Button>
        ) : (
          !setup && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={enrollOptions.isPending}
              onClick={() => void start()}
            >
              Authenticator einrichten
            </Button>
          )
        )}
      </header>

      <p className="text-sm text-muted-foreground">
        Eine App auf dem Telefon (z. B. Google Authenticator, 1Password) erzeugt alle 30 Sekunden
        einen sechsstelligen Code.
      </p>

      {setup && (
        <div className="flex flex-col gap-3 rounded-lg border border-border p-3 sm:flex-row sm:items-start sm:gap-4">
          <QrCanvas value={setup.otpauthUrl} size={168} />
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              Scanne den Code mit deiner App. Geht das Scannen nicht, gib dieses Geheimnis von Hand
              ein:
            </p>
            <code className="break-all rounded bg-muted px-2 py-1 text-xs">{setup.secret}</code>
            <FormField label="Code aus der App" htmlFor="totp-code">
              <Input
                id="totp-code"
                value={code}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                onChange={(event) => setCode(event.target.value)}
              />
            </FormField>
            <div className="flex flex-wrap gap-2">
              <Button type="button" disabled={confirm.isPending} onClick={() => void confirmCode()}>
                {confirm.isPending ? <Spinner aria-hidden /> : null}
                Bestätigen
              </Button>
              <Button type="button" variant="ghost" onClick={() => setSetup(null)}>
                Abbrechen
              </Button>
            </div>
          </div>
        </div>
      )}

      {message && <Note tone={message.tone}>{message.text}</Note>}
    </section>
  );
}

function PasswordSection() {
  const change = useChangePortalPassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeated, setRepeated] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const problem = validateNewPassword({ currentPassword, newPassword, repeated });
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    try {
      await change.mutateAsync({ currentPassword, newPassword });
      // Der Wechsel beendet serverseitig ALLE Sitzungen — auch diese. Ohne das
      // Abmelden liefe die Seite in lauter 401er, ohne zu sagen warum.
      setIsDone(true);
    } catch (caught) {
      setError(describePortalError(caught));
    }
  }

  if (isDone) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Note tone="success">
          Passwort geändert. Aus Sicherheitsgründen wurden alle Sitzungen beendet — bitte melde
          dich neu an.
        </Note>
        <Button type="button" onClick={logout}>
          Zur Anmeldung
        </Button>
      </div>
    );
  }

  return (
    <form className="flex max-w-md flex-col gap-4" onSubmit={submit}>
      <FormField label="Bisheriges Passwort" htmlFor="current-password">
        <Input
          id="current-password"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
        />
      </FormField>
      <FormField label="Neues Passwort" htmlFor="new-password">
        <Input
          id="new-password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
        />
      </FormField>
      <FormField label="Neues Passwort wiederholen" htmlFor="repeat-password">
        <Input
          id="repeat-password"
          type="password"
          autoComplete="new-password"
          value={repeated}
          onChange={(event) => setRepeated(event.target.value)}
        />
      </FormField>
      {error && <Note tone="danger">{error}</Note>}
      <Button type="submit" className="self-start" disabled={change.isPending}>
        {change.isPending ? <Spinner aria-hidden /> : null}
        Passwort ändern
      </Button>
    </form>
  );
}
